import React, { useEffect, useState } from 'react';
import { CheckCircle, ChevronRight } from 'lucide-react';
import './HeroBanner.css';

const HeroBanner = ({ onOpenApplicationModal }) => {
    const [sparkles, setSparkles] = useState([]);
    
    // Создание sparkle эффектов
    useEffect(() => {
        const createSparkles = () => {
            const newSparkles = [];
            for (let i = 0; i < 6; i++) {
                newSparkles.push({
                    id: i,
                    top: Math.random() * 100,
                    left: Math.random() * 100,
                    delay: Math.random() * 2
                });
            }
            setSparkles(newSparkles);
        };
        
        createSparkles();
        const interval = setInterval(createSparkles, 3000);
        return () => clearInterval(interval);
    }, []);

    // Современные стили для красивого хиро баннера с эффектами
    const styles = {
        container: {
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 50%, #1e40af 100%)',
            backgroundSize: '400% 400%',
            animation: 'gradientShift 8s ease infinite',
            borderRadius: '24px',
            minHeight: '500px',
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '0',
            boxShadow: '0 20px 60px rgba(30, 58, 138, 0.3)',
        },
        // Анимированные декоративные элементы
        backgroundPattern: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M30 30c0-16.569 13.431-30 30-30v60c-16.569 0-30-13.431-30-30z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '120px 120px'
        },
        // Плавающие геометрические элементы
        floatingElement1: {
            position: 'absolute',
            top: '10%',
            right: '15%',
            width: '120px',
            height: '120px',
            background: 'linear-gradient(45deg, rgba(217, 151, 6, 0.2), rgba(217, 151, 6, 0.1))',
            borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
            animation: 'float 6s ease-in-out infinite, morphing 12s ease-in-out infinite',
            filter: 'blur(1px)'
        },
        floatingElement2: {
            position: 'absolute',
            bottom: '20%',
            right: '8%',
            width: '80px',
            height: '80px',
            background: 'linear-gradient(45deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05))',
            borderRadius: '40% 60% 60% 40% / 60% 30% 70% 40%',
            animation: 'float 8s ease-in-out infinite reverse, morphing 10s ease-in-out infinite'
        },
        floatingElement3: {
            position: 'absolute',
            top: '60%',
            left: '80%',
            width: '60px',
            height: '60px',
            background: 'rgba(217, 151, 6, 0.2)',
            borderRadius: '50%',
            animation: 'pulse 4s ease-in-out infinite, glow 3s ease-in-out infinite'
        },
        // Дополнительные плавающие элементы
        floatingElement4: {
            position: 'absolute',
            top: '30%',
            left: '10%',
            width: '40px',
            height: '40px',
            background: 'linear-gradient(45deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
            borderRadius: '50%',
            animation: 'float 5s ease-in-out infinite, pulse 3s ease-in-out infinite'
        },
        floatingElement5: {
            position: 'absolute',
            bottom: '10%',
            left: '20%',
            width: '30px',
            height: '30px',
            background: 'rgba(217, 151, 6, 0.15)',
            borderRadius: '50%',
            animation: 'float 7s ease-in-out infinite reverse'
        },
        // Основной контент
        mainContent: {
            display: 'grid',
            gridTemplateColumns: '1fr 400px',
            gap: '4rem',
            alignItems: 'center',
            width: '100%',
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '4rem 2rem',
            position: 'relative',
            zIndex: 10
        },
        textSection: {
            color: 'white',
            animation: 'slideInLeft 1s ease-out'
        },
        title: {
            fontSize: '3.5rem',
            fontWeight: '800',
            lineHeight: '1.1',
            marginBottom: '1rem',
            background: 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            animation: 'slideInUp 1.2s ease-out, textGlow 4s ease-in-out infinite'
        },
        subtitle: {
            fontSize: '1.3rem',
            fontWeight: '400',
            marginBottom: '1.5rem',
            color: 'rgba(255, 255, 255, 0.9)',
            lineHeight: '1.6'
        },
        description: {
            fontSize: '1.1rem',
            lineHeight: '1.7',
            marginBottom: '2.5rem',
            color: 'rgba(255, 255, 255, 0.85)'
        },
        featuresGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '1rem',
            marginBottom: '2.5rem'
        },
        feature: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.75rem',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            transition: 'all 0.3s ease'
        },
        featureIcon: {
            color: '#d97706',
            flexShrink: 0
        },
        featureText: {
            color: 'white',
            fontSize: '0.95rem',
            fontWeight: '500'
        },
        ctaButton: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '1rem 2rem',
            background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
            color: 'white',
            borderRadius: '16px',
            fontWeight: '600',
            fontSize: '1.1rem',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            boxShadow: '0 8px 25px rgba(217, 151, 6, 0.4)',
            position: 'relative',
            overflow: 'hidden'
        },
        // Правая секция с визуализацией
        visualSection: {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
            animation: 'slideInRight 1.5s ease-out'
        },
        statsCard: {
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            padding: '2rem',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: 'white',
            animation: 'cardFloat 6s ease-in-out infinite',
            transition: 'all 0.3s ease'
        },
        statsNumber: {
            fontSize: '2.5rem',
            fontWeight: '800',
            color: '#d97706',
            marginBottom: '0.5rem'
        },
        statsLabel: {
            fontSize: '1rem',
            color: 'rgba(255, 255, 255, 0.9)',
            marginBottom: '1rem'
        },
        progressBar: {
            width: '100%',
            height: '6px',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '3px',
            overflow: 'hidden'
        },
        progressFill: {
            height: '100%',
            background: 'linear-gradient(90deg, #d97706, #f59e0b)',
            borderRadius: '3px',
            width: '85%',
            animation: 'fillProgress 2s ease-out'
        },
        serviceCard: {
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(15px)',
            borderRadius: '16px',
            padding: '1.5rem',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            color: 'white'
        },
        serviceIcon: {
            width: '50px',
            height: '50px',
            background: 'linear-gradient(135deg, #d97706, #f59e0b)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
        },
        serviceText: {
            fontSize: '0.95rem',
            lineHeight: '1.4'
        }
    };

    // Адаптивность для мобильных устройств
    useEffect(() => {
        const handleResize = () => {
            const mainContent = document.querySelector('.hero-main-content');
            if (mainContent) {
                if (window.innerWidth <= 768) {
                    mainContent.style.gridTemplateColumns = '1fr';
                    mainContent.style.gap = '2rem';
                    mainContent.style.padding = '2rem 1rem';
                } else {
                    mainContent.style.gridTemplateColumns = '1fr 400px';
                    mainContent.style.gap = '4rem';
                    mainContent.style.padding = '4rem 2rem';
                }
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div className="hero-banner" style={styles.container}>
            {/* Анимированный фоновый паттерн */}
            <div style={styles.backgroundPattern}></div>
            
            {/* Sparkle эффекты */}
            {sparkles.map((sparkle) => (
                <div
                    key={sparkle.id}
                    className="sparkle"
                    style={{
                        position: 'absolute',
                        top: `${sparkle.top}%`,
                        left: `${sparkle.left}%`,
                        animationDelay: `${sparkle.delay}s`
                    }}
                />
            ))}
            
            {/* Плавающие декоративные элементы */}
            <div style={styles.floatingElement1}></div>
            <div style={styles.floatingElement2}></div>
            <div style={styles.floatingElement3}></div>
            <div style={styles.floatingElement4}></div>
            <div style={styles.floatingElement5}></div>

            <div className="hero-main-content" style={styles.mainContent}>
                {/* Основной текстовый контент */}
                <div style={styles.textSection}>
                    <h1 style={styles.title}>
                        Пригон авто под заказ из Европы
                    </h1>
                    <h2 style={styles.subtitle}>
                        Германии, Бельгии, Голландии, Франции и других стран ЕС
                    </h2>
                    <p style={styles.description}>
                        Поможем подобрать и доставим автомобиль вашей мечты из Европы автовозом 
                        с полным таможенным оформлением и гарантией качества
                    </p>

                    <div style={styles.featuresGrid}>
                        <div 
                            className="feature-card"
                            style={styles.feature}
                        >
                            <CheckCircle size={22} style={styles.featureIcon} />
                            <span style={styles.featureText}>Официальный договор</span>
                        </div>
                        <div 
                            className="feature-card"
                            style={styles.feature}
                        >
                            <CheckCircle size={22} style={styles.featureIcon} />
                            <span style={styles.featureText}>Лучший сервис</span>
                        </div>
                        <div 
                            className="feature-card"
                            style={styles.feature}
                        >
                            <CheckCircle size={22} style={styles.featureIcon} />
                            <span style={styles.featureText}>Доставка до 8-ми дней</span>
                        </div>
                        <div 
                            className="feature-card"
                            style={styles.feature}
                        >
                            <CheckCircle size={22} style={styles.featureIcon} />
                            <span style={styles.featureText}>Гарантия юридической чистоты</span>
                        </div>
                    </div>

                    <button
                        style={styles.ctaButton}
                        onClick={onOpenApplicationModal}
                        onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-3px) scale(1.05)';
                            e.target.style.boxShadow = '0 12px 35px rgba(217, 151, 6, 0.5)';
                            e.target.style.animation = 'buttonPulse 0.6s ease-in-out';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0) scale(1)';
                            e.target.style.boxShadow = '0 8px 25px rgba(217, 151, 6, 0.4)';
                            e.target.style.animation = 'none';
                        }}
                    >
                        Оставить заявку
                        <ChevronRight size={22} />
                    </button>
                </div>

                {/* Правая секция с статистикой и услугами */}
                <div style={styles.visualSection}>
                    {/* Карточка со статистикой */}
                    <div 
                        className="stats-card"
                        style={styles.statsCard}
                    >
                        <div style={styles.statsNumber}>500+</div>
                        <div style={styles.statsLabel}>Авто доставлено в этом году</div>
                        <div style={styles.progressBar}>
                            <div style={styles.progressFill}></div>
                        </div>
                    </div>

                    {/* Карточка с услугой */}
                    <div 
                        className="service-card"
                        style={styles.serviceCard}
                    >
                        <div style={styles.serviceIcon}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 16H9m10 0h3v-3.15a1 1 0 00-.84-.99L16 11l-2.7-3.6a1 1 0 00-.8-.4H5.24a2 2 0 00-1.8 1.1l-.8 1.63A6 6 0 002 12.42V16h2"/>
                                <circle cx="6.5" cy="16.5" r="2.5"/>
                                <circle cx="16.5" cy="16.5" r="2.5"/>
                            </svg>
                        </div>
                        <div style={styles.serviceText}>
                            <strong>Полное сопровождение</strong><br />
                            От поиска до получения ключей
                        </div>
                    </div>

                    {/* Карточка с гарантией */}
                    <div 
                        className="service-card"
                        style={styles.serviceCard}
                    >
                        <div style={styles.serviceIcon}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                                <path d="M9 12l2 2 4-4"/>
                            </svg>
                        </div>
                        <div style={styles.serviceText}>
                            <strong>Проверка истории</strong><br />
                            Гарантия юридической чистоты
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HeroBanner;